import React, { useEffect, useRef, useState } from "react"
import _ from "lodash"
// PR
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { ContextMenu } from "primereact/contextmenu"
// Components
import CarouselModal, { CarouselData } from "components/table/carousel/carousel-modal"
import CarouselTrigger from "components/table/carousel/carousel-trigger"
import { field as _field } from "components/table/lib/column-field"
import SearchHeader from "components/table/search-header"
import {
  TableAutomation,
  focusFirstCellByRowIndex,
  focusCellByContent,
  focusCellByDataId,
  pageToFirst,
  getPagingInfo,
  getFocusedRowIndex,
  unfocusAllCells } from "components/table/table-automation"
import { NamedItemEdit, NumberEdit, TextEdit } from "components/table/inputs"
import { NamedItemDisplay } from "components/table/display"
import { DuplicateModal } from "components/modal/duplicate-confirmation"
// Controls
import Controls from "components/market-lease/controls/"
// Data
import automationService from "data/automation"
import carouselService from "data/carousel"
import keyService, * as chord from "data/key"
import { Area } from 'data/area-measures'
import { Downtime } from 'data/downtime'
import { FreeRent } from 'data/free-rent'
import { Increase } from 'data/increase'
import { Inflation } from 'data/inflation'
import marketLeaseService, { MarketLease } from 'data/market-lease'
import { Recovery } from 'data/named-recoveries'
import { Payout } from 'data/payout'
import propertyService, { Property } from 'data/property'
import { RecoveryGroup } from 'data/recovery-groups'
import { RecoveryPool } from 'data/recovery-pools'
import { RenewalProbability } from 'data/renewal-probability'
import { Rent } from 'data/rent'
import { ReportTag } from 'data/report-tag'
import { TenantCapital } from 'data/tenant-capital'

import { handleReferences } from "data/lib/handle-references"
import { copy, paste, canPaste, getPasteInfo } from "data/lib/copy-paste"
import toggles from "feature-toggles"
// Styles
import { MarketLeaseTable_s } from "components/market-lease/market-lease-styles"

export type ContextRow = {
  marketleaseid: number
  propertyid: number
  code: string
  systeminput?: string
}

type PropTypes = {
  tableId: string
  data: MarketLease[]
  areaMeasures: Area[]
  columns: string[]
  downtime: Downtime[]
  rent: Rent[]
  freeRent: FreeRent[]
  increase: Increase[]
  inflation: Inflation[]
  namedRecoveries: Recovery[]
  payout: Payout[]
  property: Property
  recoveryGroups: RecoveryGroup[]
  recoveryPools: RecoveryPool[]
  renewalProbability: RenewalProbability[]
  reportTags: ReportTag[]
  ti: TenantCapital[]
  lc: TenantCapital[]
  oc: TenantCapital[]
  selectedState: [ ContextRow[], ( selected: ContextRow[] ) => void ]
  flags: { deleteFlag: number, pasteFlag: number }
}

const MarketLeaseTable = (props: PropTypes) => {
  const [ selected, setSelected ] = props.selectedState

  const usePrevious = (value) =>{
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  const traceMenuItem = {
    label: 'Trace', icon: 'pi pi-fw pi-search', command: e=> {
      const refreshFn = () => marketLeaseService.fetchMarketLeases(contextRow.propertyid)
      handleReferences('marketlease', contextRow.marketleaseid, {refreshFn});
    }
  }

  const nonSystemMenu = [
    {label: 'Copy Cell', icon: 'pi pi-fw pi-copy', command: e => { if(contextRow.systeminput === "systemImmutable") return;
      copy(contextCell, contextRow)
    }},
    {label: 'Duplicate Row', icon: 'pi pi-fw pi-clone', command: e => { if(contextRow.systeminput) return;
      marketLeaseService.copy(contextRow.propertyid, contextRow.marketleaseid, contextRow.code)
    }},
    {label: 'Duplicate Row n Times', icon: 'pi pi-fw pi-clone', command: async e => { if(contextRow.systeminput) return;
      showDuplicateConfirmation(async (n) => {
        const marketleases = await marketLeaseService.copies(contextRow.propertyid, contextRow.marketleaseid, n)
        !_.isEmpty(selected) && setSelected(selected.concat(marketleases as ContextRow[]))
      })
    }},
    {label: selected.length ? 'Delete Rows' : 'Delete Row', icon: 'pi pi-fw pi-trash', command: e => { if ( contextRow.systeminput ) return;
      const pager         = getPagingInfo(props.tableId);
      const currentRowNum = getFocusedRowIndex() + 1
      const currentRecordIndex = (pager.currentPage > 1 ? (pager.currentPage - 1) * pager.pageSize : 0) + currentRowNum - 1
      showDeleteConfirmation(contextRow, currentRecordIndex + 1)
    }},
    traceMenuItem
  ]

  const areaMeasures:object[]       = props.areaMeasures
  const downtime:object[]           = props.downtime
  const freeRent:object[]           = props.freeRent
  const increase:object[]           = props.increase
  const inflation:object[]          = props.inflation
  const namedRecoveries:object[]    = props.namedRecoveries
  const payout:object[]             = props.payout
  const property:object             = props.property
  const recoveryGroups:object[]     = props.recoveryGroups
  const recoveryPools:object[]      = props.recoveryPools
  const renewalProbability:object[] = props.renewalProbability
  const rent:object[]               = props.rent
  const reportTags:object[]         = props.reportTags
  const ti:object[]                 = props.ti
  const lc:object[]                 = props.lc
  const oc:object[]                 = props.oc

  const [carouselData, updateCarouselData] = useState<CarouselData|null>(null);
  const [searchFilter, setSearchFilter] = useState<string>("")
  const [contextRow, setContextRow] = useState<ContextRow>({marketleaseid: 0, propertyid: 0, code: ''})
  const [contextCell, setContextCell] = useState<object|null>(null)
  const [deleteConfirmation, setDeleteConfirmation] = useState<JSX.Element | null>(null)
  const [duplicateConfirmation, setDuplicateConfirmation] = useState<JSX.Element | null>(null)
  const menuRef = useRef<ContextMenu | null>(null)
  const [menu, setMenu] = useState<[]|object[]>(nonSystemMenu)
  const prevSearch = usePrevious(searchFilter);
  const tableRef = useRef() as React.MutableRefObject<DataTable>
  const field = _field('marketlease')

  useEffect(() => {
    const carouselSubscription = carouselService.stream().subscribe(data => {
      updateCarouselData(data);
    })

    const marketLeaseSubscription = marketLeaseService.eventStream().subscribe(e => {
      switch(true) {
        case e.type === "MARKET_LEASE_CREATED":
          setTimeout(() => pageToFirst(props.tableId), 1)
          setTimeout(() => {
            focusFirstCellByRowIndex(props.tableId, 0)
          }, 500)
          break
        case e.type === "MARKET_LEASE_COPIED":
          setTimeout(() => {
            focusCellByContent(props.tableId, e.payload.code)
          }, 500)
      }
    })

    const keySubscription = keyService.stream().subscribe(k => {
      switch(true) {
        case k.chord === chord.CTRL_SHIFT_PLUS && k.down === true:
          if(!window[`${props.tableId}_carousel_in_focus`]) {
            unfocusAllCells()
            marketLeaseService.create(propertyService.getStoredPropertyId())
          }
          break
        case k.chord === chord.CTRL_SHIFT_MINUS && k.down === true:
          if(!window[`${props.tableId}_carousel_in_focus`]) {
            const pager         = getPagingInfo(props.tableId);
            const currentRowNum = getFocusedRowIndex() + 1
            const currentRecordIndex = (pager.currentPage > 1 ? (pager.currentPage - 1) * pager.pageSize : 0) + currentRowNum - 1
            const records       = tableRef.current.props.value!;
            const currentRow    = records[currentRecordIndex];
            if (!currentRow.systeminput) showDeleteConfirmation({marketleaseid: currentRow.marketleaseid, propertyid: currentRow.propertyid, code: currentRow.code}, currentRecordIndex + 1)
          }
          break
      }
    })

    return function cleanup() {
      carouselSubscription.unsubscribe()
      marketLeaseSubscription.unsubscribe()
      keySubscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (prevSearch !== "" && searchFilter === "") _.invoke(tableRef, 'current.filter', '', 'contains');
    else if (searchFilter !== "") _.invoke(tableRef, 'current.filter', searchFilter, 'contains');
    else return
  }, [searchFilter])

  useEffect(() => {
    setMenu(nonSystemMenu)
  }, [contextRow])

  const persist = (value, field, rowData) => {
    const marketLeases = _.omit(_.cloneDeep(rowData), 'index') // strip fields added for table view
    _.set(marketLeases, field, value)
    if (field === 'subsequentmarketdetail.marketleaseid' && !_.isNil(value)) _.set(marketLeases, 'subsequentmarketdetail.state', 'basic')
    marketLeaseService.patch(marketLeases);
    carouselService.updateRowData(marketLeases);
  }

  const refresh = async () => {
    const { propertyid } = contextRow
    const mfs = await marketLeaseService.fetchMarketLeases(propertyid)
    setSelected( _.intersectionBy( mfs, selected, 'marketleaseid' ) )
  }

  useEffect(() => {
    if ( canPaste( contextCell, selected ) ) {
      nonSystemMenu.splice( 1, 0, {
        label: selected.length ? 'Paste to Rows' : 'Paste Cell', icon: 'pi pi-fw pi-file', command: e => {
          if (selected.length > 1 ) {
            showPasteConfirmation( contextRow, -1 )
            return
          }
          paste(contextCell, contextRow, refresh, 'marketlease', selected)
      }})
    }
    contextRow.systeminput === "systemImmutable" ? setMenu([traceMenuItem]) : setMenu(nonSystemMenu)
  }, [contextCell, selected.length])

  useEffect( () => {
    if ( !props.flags.deleteFlag ) return //skip first time
    showDeleteConfirmation(_.first(selected), -1 )
  }, [ props.flags.deleteFlag ] )

  const showDeleteConfirmation = (record: ContextRow, currentRow: number) => {
    automationService.broadcast("MODAL_OPENING", {})
    setDeleteConfirmation(Controls.DeleteConfirmation({
      show: true,
      message: `Would you like to delete the ${selected.length} selected rows? Note this cannot be undone.`,
      close: () => {
        setDeleteConfirmation(null)
        setTimeout(() => automationService.broadcast("MODAL_CLOSING", {}), 200)
      },
      confirm: () => {
        if (selected.length > 1 ) {
          const propertyid = _.first(selected).propertyid
          marketLeaseService.deleteMultiple(propertyid, _.map(selected, 'marketleaseid'))
        } else {
          marketLeaseService.deleteOne(record.propertyid, record.marketleaseid)
        }
        setDeleteConfirmation(null)
        setTimeout(() => {
          focusFirstCellByRowIndex(props.tableId, currentRow - 2 > 0 ? currentRow - 2 : 0)
        }, 200)
      },
      rowNum: currentRow,
      code: record.code,
      selected
    }))
  }

  const showDuplicateConfirmation = (copy: (n: number) => Promise<void>) => {
    setDuplicateConfirmation(
      <DuplicateModal
        close={() => {setDuplicateConfirmation(null); focusCellByDataId( props.tableId, contextRow.marketleaseid)}}
        copy={copy}
        show={true}
        title={contextRow.code}
      />
    )
  }

  useEffect( () => {
    if ( !props.flags.pasteFlag ) return //skip first time
    if ( !canPaste( contextCell, selected ) ) return
    showPasteConfirmation( contextRow, -1 )
  }, [ props.flags.pasteFlag ] )

  const showPasteConfirmation = ( record: ContextRow, currentRow: number ) => {
    automationService.broadcast( "MODAL_OPENING", {} )
    setDeleteConfirmation( Controls.DeleteConfirmation( {
      show: true,
      title: 'Paste',
      message: `Would you like to paste ${getPasteInfo()._meta.field} data to the ${selected.length} selected rows? Note this cannot be undone.`,
      close: () => {
        setDeleteConfirmation( null )
        setTimeout( () => automationService.broadcast( "MODAL_CLOSING", {} ), 200 )
      },
      confirm: () => {
        paste( contextCell, contextRow, refresh, 'marketlease', selected )
        setDeleteConfirmation(null)
        setTimeout(() => {
          focusFirstCellByRowIndex(props.tableId, currentRow - 2 > 0 ? currentRow - 2 : 0)
        }, 200)
      },
      rowNum: currentRow,
      code: record.code,
      selected
    }))
  }

  function updateContextMenu(originalEvent){
    _.invoke(menuRef.current, "show", originalEvent)
  }

  const rowClassName = (rowData) => {
    return {'' : rowData}
  }

  const data = props.data
  const marketLeaseOptions = _.map(data, (i) => { return {label: i.code, value: i.marketleaseid} })

  return (
    <div className="market-lease-table-container">
      <SearchHeader setSearchFilter={setSearchFilter} />
      <ContextMenu model={menu} ref={menuRef} appendTo={document.querySelector("body")} onHide={() => {}}/>
      <MarketLeaseTable_s id={`${props.tableId}-scrollable-container`}>
        { data.length > 0 && <DataTable
          globalFilter={searchFilter}
          id={props.tableId}
          ref={tableRef}
          value={data}
          paginator={true}
          rows={20}
          rowsPerPageOptions={[10, 20, 50, 100, 500]}
          scrollable={true}
          frozenWidth={ toggles.multiselect() ? "450px" : "405px" }
          paginatorPosition="bottom"
          rowClassName={rowClassName}
          onContextMenu={e => menuRef.current ? updateContextMenu(e.originalEvent) : null}
          contextMenuSelection={contextRow}
          onContextMenuSelectionChange={e => {
            setSelected([])
            setContextRow(e.value)
            setContextCell(e.originalEvent.target)
          }}
          stateStorage="custom"
          customSaveState={state => localStorage.setItem("primereact-table-preferences", JSON.stringify({rows: state.rows}))}
          customRestoreState={() => JSON.parse(localStorage.getItem("primereact-table-preferences")!)}
          selection={selected} onSelectionChange={(e) => {
            const selected = _.filter(e.value, (it) => it.systeminput !== "systemImmutable")
            setSelected(selected)
          }}
        >
          { toggles.multiselect() && <Column selectionMode="multiple" frozen={ true } style={ { minWidth: "45px", paddingLeft: "8px" } } /> }
          <Column header="#" frozen={true} style={{minWidth: "45px"}}
            body={(rowData, props) => props.rowIndex + 1}
          />
          <Column style={{minWidth: "180px", maxWidth: "180px"}} frozen={true} className="p-editable-column"
            header="Code"
            {...field('code')}
            editor={props => TextEdit({persist, ...props})}
          />
          <Column style={{minWidth: "180px", maxWidth: "180px"}} frozen={true} className="p-editable-column"
            header="Name"
            {...field('name')}
            editor={props => TextEdit({persist, ...props})}
          />
          {props['columns'].includes('Term (Mos)') && <Column style={{minWidth: "100px", maxWidth: "100px"}} className="p-editable-column"
            header="Term (Mos)"
            {...field('termdetail.term')}
            editor={props => NumberEdit({persist, ...props, min: 0, max: 1200})}
          />}
          {props['columns'].includes('Downtime') && <Column style={{minWidth: "180px"}} className="p-editable-column"
            header="Downtime"
            {...field('downtimedetail', (rowData, props) => { return(
              <CarouselTrigger field="downtimedetail" rowData={rowData} title="Downtime"
                persist={persist}
                controls={{
                  basic: {control: Controls.DowntimeBasic, name: 'Basic'},
                  namedItem: {control: Controls.DowntimeNamedItem, name: 'Named Item'}
                }}
                namedItems={{downtime}}
                componentAtRest={()=>{
                  if (!rowData['downtimedetail'] || rowData['downtimedetail']?.['state'] === 'basic') { return rowData['downtimedetail']?.['downtime'] ? `${rowData['downtimedetail']['downtime']} Mos` : '-' }
                  if (rowData['downtimedetail']?.['state'] === 'namedItem') return _.find(downtime, {downtimeid: rowData['downtimedetail']['downtimeNamedItemId']})?.['code']
                }}
                hover={{
                  basic: [{key: ['downtimedetail', 'downtime'], label: 'Downtime (Mos)'}],
                  namedItem: [{key: ['downtimedetail', 'downtimeNamedItemId'], label: 'Downtime'}],
                }}
              />)}
            )}
          />}
          {props['columns'].includes('Renewal Probability') && <Column style={{minWidth: "180px"}} className="p-editable-column"
            header="Renewal Probability"
            {...field('renewaldetail', (rowData, props) => { return(
              <CarouselTrigger field="renewaldetail" rowData={rowData} title="Renewal Probability"
                persist={persist}
                controls={{
                  basic: {control: Controls.RenewalProbBasic, name: 'Basic'},
                  namedItem: {control: Controls.RenewalProbNamedItem, name: 'Named Item'}
                }}
                namedItems={{renewalProbability}}
                defaults={{...property['defaultsmarketlease']}}
                componentAtRest={() => {
                  if (!rowData['renewaldetail'] || rowData['renewaldetail']?.['state'] === 'basic') { return rowData['renewaldetail']?.['renewalProbability'] ? `${rowData['renewaldetail']['renewalProbability']}%` : '-' }
                  if (rowData['renewaldetail']?.['state'] === 'namedItem') return _.find(renewalProbability, {renewalprobabilityid: rowData['renewaldetail']['renewalProbabilityNamedItemId']})?.['code']
                }}
                hover={{
                  injected: Controls.RenewalProbHover
                }}
              />)}
            )}
          />}
          {props['columns'].includes('Rent') && <Column style={{minWidth: "180px"}} className="p-editable-column"
            header="Rent"
            {...field('rentdetail', (rowData, props) => { return(
              <CarouselTrigger field="rentdetail" rowData={rowData} title="Rent"
                persist={persist}
                controls={{
                  basic: {control: Controls.RentBasic, name: 'Basic'},
                  namedItem: {control: Controls.RentNamedItem, name: 'Named Item'},
                  advanced: {control: Controls.RentAdvanced, name: 'Advanced'}
                }}
                namedItems={{
                  increase,
                  inflation,
                  rent
                }}
                defaults={{...property['defaultsmarketlease']}}
                componentAtRest={() => {
                  if(!rowData['rentdetail'] || rowData['rentdetail']?.['state'] === 'basic') { return rowData['rentdetail']?.['new'] ? `${rowData['rentdetail']['new']} | ${rowData['rentdetail']['renewal']} ${_.find(increase, {increaseid: rowData['rentdetail']['increaseNamedItemId']})?.['code'] || ''}` : '-' }
                  if(rowData['rentdetail']?.['state'] === 'advanced') { return rowData['rentdetail']?.['newAmt'] ? `${rowData['rentdetail']['newAmt']} | ${rowData['rentdetail']['renewalAmt'] || ''}` : 'Advanced' }
                  if(rowData['rentdetail']?.['state'] === 'namedItem') return _.find(rent, {rentid: rowData['rentdetail']['rentNamedItemId']})?.['code']
                }}
                hover={{
                  basic: [
                    {key: ['rentdetail', 'new'], label: 'New'},
                    {key: ['rentdetail', 'renewal'], label: 'Renewal'},
                    {key: ['rentdetail', 'amtType'], label: 'Amt Type'},
                    {key: ['rentdetail', 'inflation', 'inflationNamedItemId'], label: 'Inflation'},
                    {key: ['rentdetail', 'increaseNamedItemId'], label: 'Increase'},
                  ],
                  namedItem: [{key: ['rentdetail', 'rentNamedItemId'], label: 'Rent'},{key: ['rentdetail', 'increaseNamedItemId'], label: 'Increase'}],
                  advanced: [
                    {key: ['rentdetail', 'newAmt'], label: 'New Amt'},
                    {key: ['rentdetail', 'newAmtType'], label: 'New Amt Type'},
                    {key: ['rentdetail', 'renewalAmt'], label: 'Renewal Amt'},
                    {key: ['rentdetail', 'renewalAmtType'], label: 'Renewal Amt Type'},
                    {key: ['rentdetail', 'inflation', 'inflationNamedItemId'], label: 'Inflation'},
                    {key: ['rentdetail', 'increaseNamedItemId'], label: 'Increase'}
                  ]
                }}
              />)}
            )}
          />}
          {props['columns'].includes('Free Rent') && <Column style={{minWidth: "180px"}} className="p-editable-column"
            header="Free Rent"
            {...field('freerentdetail', (rowData, props) => { return(
              <CarouselTrigger field="freerentdetail" rowData={rowData} title="Free Rent"
                persist={persist}
                controls={{
                  basic: {control: Controls.FreeRentBasic, name: 'Basic'},
                  namedItem: {control: Controls.FreeRentNamedItem, name: 'Named Item'}
                }}
                namedItems={{freeRent}}
                defaults={{...property['defaultsmarketlease']}}
                componentAtRest={()=>{
                  if (!rowData['freerentdetail'] || rowData['freerentdetail']?.['state'] === 'basic') { return rowData['freerentdetail'] ? `${rowData['freerentdetail']?.['new'] || 'N/A'} | ${rowData['freerentdetail']['renewal'] || 'N/A'} ${rowData['freerentdetail']['amtType']} ${rowData['freerentdetail']['revenue']}` : '-' }
                  if (rowData['freerentdetail']?.['state'] === 'namedItem') return _.find(freeRent, {freerentid: rowData['freerentdetail']['freeRentNamedItemId']})?.['code']
                }}
                hover={{
                  basic: [
                    {key: ['freerentdetail', 'new'], label: 'New'},
                    {key: ['freerentdetail', 'renewal'], label: 'Renewal'},
                    {key: ['freerentdetail', 'amtType'], label: 'Amt Type'},
                    {key: ['freerentdetail', 'revenue'], label: 'Revenue'},
                  ],
                  namedItem: [{key: ['freerentdetail', 'freeRentNamedItemId'], label: 'Free Rent'}],
                }}
              />)}
            )}
          />}
          {props['columns'].includes('Recovery') && <Column style={{minWidth: "180px"}} className="p-editable-column"
            header="Recovery"
            {...field('recoverydetail', (rowData, props) => {
              const count = `Recovery Count:
              ${(props.rowData['recoverydetail']?.['namedRecoveryItems']?.length || 0)
              + (props.rowData['recoverydetail']?.['namedRecoveryGroupItems']?.length || 0)
              + (props.rowData['recoverydetail']?.['prorataRecoveryItems']?.length || 0)
              + (props.rowData['recoverydetail']?.['amtItems']?.length || 0)
              + (props.rowData['recoverydetail']?.['detailedAmtItems']?.length || 0)
              + (props.rowData['recoverydetail']?.['tenantMgmtFeeItems']?.length || 0)
              }`
              return(
              <CarouselTrigger field="recoverydetail" rowData={rowData} title={count}
                persist={persist}
                controls={{
                  namedRecoveryItems: {control: Controls.RecoveriesNamed, name: 'Named Recoveries'},
                  namedRecoveryGroupItems: {control: Controls.RecoveryGroups, name: 'Recovery Groups'},
                  prorataRecoveryItems: {control: Controls.RecoveriesProRata, name: 'Pro Rata'},
                  amtItems: {control: Controls.RecoveriesAmt, name: 'Amt'},
                  detailedAmtItems: {control: Controls.RecoveriesDetailedAmt, name: 'Detailed Amt'},
                  tenantMgmtFeeItems: {control: Controls.RecoveriesTenantMgmtFee, name: 'Tenant Mgmt Fee'}
                }}
                namedItems={{areaMeasures, increase, inflation, payout, tenantCapital: ti, namedRecoveries, recoveryGroups, recoveryPools, reportTags}}
                defaults={{...property['defaultsmarketlease']}}
                componentAtRest={()=>{
                  return count
                }}
                hover={{recoverydetail: props.rowData}}
                table="marketlease"
              />)}
            )}
          />}
          {props['columns'].includes('T.I.') && <Column style={{minWidth: "180px"}} className="p-editable-column"
            header="T.I."
            {...field('tidetail', (rowData, props) => { return(
              <CarouselTrigger field="tidetail" rowData={rowData} title="T.I."
                persist={persist}
                controls={{
                  basic: {control: Controls.TiBasic, name: 'Basic'},
                  namedItem: {control: Controls.TiNamedItem, name: 'Named Item'}
                }}
                namedItems={{inflation, payout, tenantCapital: ti}}
                defaults={{...property['defaultsmarketlease']}}
                componentAtRest={()=>{
                  if (!rowData['tidetail'] || rowData['tidetail']?.['state'] === 'basic') { return (rowData['tidetail'] && rowData['tidetail']?.['new'] || rowData['tidetail']?.['renewal'])  ? `${rowData['tidetail']?.['new'] || ''} | ${rowData['tidetail']['renewal'] || ''} ${rowData['tidetail']['amtType'] || ''}` : '-' }
                  if (rowData['tidetail']?.['state'] === 'namedItem') return _.find(ti, {tenantcapitalid: rowData['tidetail']['tenantCapitalNamedItemId']})?.['code']
                }}
                hover={{
                  basic: [
                    {key: ['tidetail', 'new'], label: 'New'},
                    {key: ['tidetail', 'renewal'], label: 'Renewal'},
                    {key: ['tidetail', 'amtType'], label: 'Amt Type'},
                    {key: ['tidetail', 'inflation', 'inflationNamedItemId'], label: 'Inflation'},
                    {key: ['tidetail', 'payout', 'payoutNamedItemId'], label: 'Payout'},
                  ],
                  namedItem: [{key: ['tidetail', 'tenantCapitalNamedItemId'], label: 'T.I.'}],
                }}
              />)}
            )}
          />}
          {props['columns'].includes('L.C.') && <Column style={{minWidth: "180px"}} className="p-editable-column"
            header="L.C."
            {...field('lcdetail', (rowData, props) => { return(
              <CarouselTrigger field="lcdetail" rowData={rowData} title="L.C."
                persist={persist}
                controls={{
                  basic: {control: Controls.LCBasic, name: 'Basic'},
                  namedItem: {control: Controls.LCNamedItem, name: 'Named Item'},
                }}
                namedItems={{
                  inflation,
                  payout,
                  tenantCapital: lc
                }}
                defaults={{...property['defaultsmarketlease']}}
                componentAtRest={() => {
                  if(!rowData['lcdetail'] || rowData['lcdetail']?.['state'] === 'basic') { return rowData['lcdetail'] ? `${rowData['lcdetail']?.['new']} | ${rowData['lcdetail']['renewal']} ${rowData['lcdetail']['amtType'] || ''}` : '-' }
                  if(rowData['lcdetail']?.['state'] === 'namedItem') return _.find(lc, {tenantcapitalid: rowData['lcdetail']['tenantCapitalNamedItemId']})?.['code']
                }}
                hover={{
                  basic: [
                    {key: ['lcdetail', 'new'], label: 'New'},
                    {key: ['lcdetail', 'renewal'], label: 'Renewal'},
                    {key: ['lcdetail', 'amtType'], label: 'Amt Type'},
                    {key: ['lcdetail', 'inflation', 'inflationNamedItemId'], label: 'Inflation'},
                    {key: ['lcdetail', 'payout', 'payoutNamedItemId'], label: 'Payout'},
                  ],
                  namedItem: [{key: ['lcdetail', 'tenantCapitalNamedItemId'], label: 'L.C.'}],
                }}
                />)}
              )}
          />}
          {props['columns'].includes('Other Capital') && <Column style={{minWidth: "180px"}} className="p-editable-column"
            header="Other Capital"
            {...field('othercapitaldetail', (rowData, props) => { return(
              <CarouselTrigger field="othercapitaldetail" rowData={rowData} title="O.C."
                persist={persist}
                controls={{
                  basic: {control: Controls.OCBasic, name: 'Basic'},
                  namedItem: {control: Controls.OCNamedItem, name: 'Named Item'},
                }}
                namedItems={{
                  inflation,
                  payout,
                  tenantCapital: oc
                }}
                defaults={{...property['defaultsmarketlease']}}
                componentAtRest={() => {
                  if(!rowData['othercapitaldetail'] || rowData['othercapitaldetail']?.['state'] === 'basic') { return rowData['othercapitaldetail'] ? `${rowData['othercapitaldetail']?.['new']} | ${rowData['othercapitaldetail']['renewal']} ${rowData['othercapitaldetail']['amtType'] || ''}` : '-' }
                  if(rowData['othercapitaldetail']?.['state'] === 'namedItem') return _.find(oc, {tenantcapitalid: rowData['othercapitaldetail']['tenantCapitalNamedItemId']})?.['code']
                }}
                hover={{
                  basic: [
                    {key: ['othercapitaldetail', 'new'], label: 'New'},
                    {key: ['othercapitaldetail', 'renewal'], label: 'Renewal'},
                    {key: ['othercapitaldetail', 'amtType'], label: 'Amt Type'},
                    {key: ['othercapitaldetail', 'inflation', 'inflationNamedItemId'], label: 'Inflation'},
                    {key: ['othercapitaldetail', 'payout', 'payoutNamedItemId'], label: 'Payout'},
                  ],
                  namedItem: [{key: ['othercapitaldetail', 'tenantCapitalNamedItemId'], label: 'O.C.'}],
                }}
                />)}
              )}
          />}
          {props['columns'].includes('Sales') && <Column style={{minWidth: "180px"}} className="p-editable-column"
            header="Sales"
            {...field('salesdetail', (rowData, props) => { return(
              <CarouselTrigger field="salesdetail" rowData={rowData} title="Sales"
                persist={persist}
                controls={{
                  basic: {control: Controls.SalesBasic, name: 'Basic'},
                  inherit: {control: Controls.SalesInherit, name: 'Inherit'}
                }}
                namedItems={{inflation}}
                defaults={{...property['defaultsmarketlease']}}
                componentAtRest={() => {
                  if(!rowData['salesdetail'] || rowData['salesdetail']?.['state'] === 'basic') { return rowData['salesdetail']?.['amt'] ? `${rowData['salesdetail']?.['amt']}${rowData['salesdetail']['amtType'] || ''}` : '-' }
                  else return 'Inherit'
                }}
                hover={{
                  basic: [
                    {key: ['salesdetail', 'amt'], label: 'Amt'},
                    {key: ['salesdetail', 'amtType'], label: 'Amt Type'},
                    {key: ['salesdetail', 'inflation', 'inflationNamedItemId'], label: 'Inflation'}
                  ],
                }}
                />)}
              )}
          />}
          {props['columns'].includes('Breakpoint') && <Column style={{minWidth: "100px"}} className="p-editable-column"
            header="Breakpoint"
            {...field('breakpointdetail.breakPoint')}
          />}
          {props['columns'].includes('Overage') && <Column style={{minWidth: "180px"}} className="p-editable-column"
            header="Overage"
            {...field('overagedetail', (rowData, props) => { return(
              <CarouselTrigger field="overagedetail" rowData={rowData} title="Overage"
                persist={persist}
                controls={{
                  basic: {control: Controls.OverageBasic, name: 'Basic'},
                  inherit: {control: Controls.OverageInherit, name: 'Inherit'}
                }}
                defaults={{...property['defaultsmarketlease']}}
                componentAtRest={() => {
                  if(!rowData['overagedetail'] || rowData['overagedetail']?.['state'] === 'basic') { return rowData['overagedetail']?.['percent'] ? `${rowData['overagedetail']?.['percent']}%` : '-' }
                  else return 'Inherit'
                }}
                hover={{
                  basic: [
                    {key: ['overagedetail', 'percent'], label: 'Amt (%)'}
                  ]
                }}
                />)}
              )}
          />}
          {props['columns'].includes('Subsequent Market') && <Column style={{minWidth: "180px"}} className="p-editable-column"
            header="Subsequent Market"
            {...field('subsequentmarketdetail.marketleaseid', (rowData, props) => NamedItemDisplay({options: marketLeaseOptions, rowData, ...props}))}
            editor={props => NamedItemEdit({persist, ...props, options: marketLeaseOptions, rowId: 'marketleaseid', onClear: ()=>persist(null, 'subsequentmarketdetail', props.rowData)})}
          />}
        </DataTable> }
        { carouselData && carouselData['show'] && <CarouselModal code="code" data={carouselData} key="carousel-modal" width={1310}/> }
        { deleteConfirmation }
        { duplicateConfirmation }
        { props.data.length ? <TableAutomation tableId={props.tableId}/> : null }
        <span className="selection-count">{ selected.length ? `Selected Rows: ${selected.length}` : null }</span>
      </MarketLeaseTable_s>
    </div>
  )
}

export default MarketLeaseTable
